import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useState } from "react";
import { X } from "lucide-react";
import { FileUpload } from "./FileUpload";

const serviceSchema = z.object({
  title_dk: z.string().min(1, "Danish title is required"),
  title_en: z.string().min(1, "English title is required"),
  description_dk: z.string().min(1, "Danish description is required"),
  description_en: z.string().min(1, "English description is required"),
  short_description_dk: z.string().optional(),
  short_description_en: z.string().optional(),
  icon: z.string().optional(),
  benefits_dk: z.array(z.string()).optional(),
  benefits_en: z.array(z.string()).optional(),
  published: z.boolean().default(false),
});

type ServiceFormValues = z.infer<typeof serviceSchema>;

interface ServiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ServiceFormValues) => void;
  initialData?: any;
  isLoading?: boolean;
}

export const ServiceForm = ({ open, onOpenChange, onSubmit, initialData, isLoading }: ServiceFormProps) => {
  const [benefitsDk, setBenefitsDk] = useState<string[]>(initialData?.benefits_dk || []);
  const [benefitsEn, setBenefitsEn] = useState<string[]>(initialData?.benefits_en || []);
  const [newBenefitDk, setNewBenefitDk] = useState("");
  const [newBenefitEn, setNewBenefitEn] = useState("");

  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceSchema),
    defaultValues: initialData || {
      title_dk: "",
      title_en: "",
      description_dk: "",
      description_en: "",
      short_description_dk: "",
      short_description_en: "",
      icon: "",
      benefits_dk: [],
      benefits_en: [],
      published: false,
    },
  });

  const handleSubmit = (data: ServiceFormValues) => {
    onSubmit({
      ...data,
      benefits_dk: benefitsDk,
      benefits_en: benefitsEn,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialData ? "Edit Service" : "Create Service"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title (Danish)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="title_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title (English)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="description_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Danish)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (English)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="short_description_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Description (Danish)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="short_description_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Description (English)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Icon (Lucide icon name)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., Target, Lightbulb, Users" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <FormLabel>Benefits (Danish)</FormLabel>
                <div className="flex gap-2">
                  <Input
                    value={newBenefitDk}
                    onChange={(e) => setNewBenefitDk(e.target.value)}
                    placeholder="Add benefit"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      if (newBenefitDk.trim()) {
                        setBenefitsDk([...benefitsDk, newBenefitDk]);
                        setNewBenefitDk("");
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                <div className="space-y-1">
                  {benefitsDk.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2 bg-muted p-2 rounded">
                      <span className="flex-1 text-sm">{benefit}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setBenefitsDk(benefitsDk.filter((_, i) => i !== index))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <FormLabel>Benefits (English)</FormLabel>
                <div className="flex gap-2">
                  <Input
                    value={newBenefitEn}
                    onChange={(e) => setNewBenefitEn(e.target.value)}
                    placeholder="Add benefit"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      if (newBenefitEn.trim()) {
                        setBenefitsEn([...benefitsEn, newBenefitEn]);
                        setNewBenefitEn("");
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                <div className="space-y-1">
                  {benefitsEn.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2 bg-muted p-2 rounded">
                      <span className="flex-1 text-sm">{benefit}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setBenefitsEn(benefitsEn.filter((_, i) => i !== index))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <FormField
              control={form.control}
              name="published"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>Published</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : initialData ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
